import '@beyondtrust/btux-iconography-web';
import { localized, msg } from '@lit/localize';
import { LitElement, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';
import {
  ADMIN_SLUG,
  AI_COWORKERS_SLUG,
  ENTITLE_SLUG,
  EPML_SLUG,
  INSIGHTS_SLUG,
  PASSWORD_SAFE_SLUG,
  PLATFORM_SLUG,
  PMC_SLUG,
  PRIVILEGED_REMOTE_ACCESS_SLUG,
  ProductMenuItem,
  REMOTE_SUPPORT_SLUG,
  SECRETS_SLUG,
  SiteData,
} from '../../types';
import '../menu/defaults';
import '../menu/menu/menu';
import '../product-header-link/product-header-link';
import '../product-icon/product-icon';
import '../site-selector/site-selector';
import '../user-profile/user-profile';
import styles from './overlay-menu-navigation-shell.styles.scss';

export const OVERLAY_MENU_NAVIGATION_SHELL_TAG = 'platform-overlay-menu-navigation-shell' as const;

/**
 * Default top-bar navigation shell. Renders the platform menu with its product
 * slots, the product header link, and the end-bar (site selector, help, user profile).
 *
 * This is the component the top-level `platform-header` renders by default — when
 * `navigation-layout` is unset or `"overlay"`. The sidebar-menu shell replaces it when a product
 * sets `navigation-layout="sidebar"`. Extracted verbatim from `platform-header`'s render to
 * preserve existing behavior exactly.
 */
@customElement(OVERLAY_MENU_NAVIGATION_SHELL_TAG)
@localized()
export class PlatformOverlayMenuNavigationShell extends LitElement {
  static override styles = [styles];

  @property({ attribute: false })
  productMenuItems: ProductMenuItem[] = [];

  @property({ attribute: false })
  currentProduct!: ProductMenuItem;

  @property({ attribute: false })
  site!: SiteData;

  @property()
  activeRoute?: string;

  /** Deployment environment badge text (e.g. "Staging"/"Preview"); empty in production. */
  @property()
  environmentLabel = '';

  override render() {
    const availableProducts = this.productMenuItems.map((p) => p.slug);

    return html`
      <div class="platform-bar-start">
        <platform-menu .productMenuItems=${this.productMenuItems} .currentSite=${this.site}>
          <slot name="admin-menu" slot="admin-menu">
            ${when(
              availableProducts.includes(ADMIN_SLUG),
              () => html`<platform-api-menu product=${ADMIN_SLUG}></platform-api-menu>`,
              () => nothing,
            )}
          </slot>
          <slot name="platform-menu" slot="platform-menu">
            ${when(
              availableProducts.includes(PLATFORM_SLUG),
              () => html`<platform-default-platform-menu></platform-default-platform-menu>`,
              () => nothing,
            )}
          </slot>

          <slot name="insights-menu" slot="insights-menu">
            ${when(
              availableProducts.includes(INSIGHTS_SLUG),
              () => html`<platform-api-menu product=${INSIGHTS_SLUG}></platform-api-menu>`,
              () => nothing,
            )}
          </slot>

          <slot name="epml-menu" slot="epml-menu">
            ${when(
              availableProducts.includes(EPML_SLUG),
              () => html`<platform-api-menu product=${EPML_SLUG}></platform-api-menu>`,
              () => nothing,
            )}
          </slot>

          <slot name="pmc-menu" slot="pmc-menu">
            ${when(
              availableProducts.includes(PMC_SLUG),
              () => html`<platform-api-menu product=${PMC_SLUG}></platform-api-menu>`,
              () => nothing,
            )}
          </slot>

          <slot name="passwordsafe-menu" slot="passwordsafe-menu">
            ${when(
              availableProducts.includes(PASSWORD_SAFE_SLUG),
              () => html`<platform-api-menu product=${PASSWORD_SAFE_SLUG}></platform-api-menu>`,
              () => nothing,
            )}
          </slot>

          <slot name="privileged-remote-access-menu" slot="privileged-remote-access-menu">
            ${when(
              availableProducts.includes(PRIVILEGED_REMOTE_ACCESS_SLUG),
              () => html`<platform-api-menu product=${PRIVILEGED_REMOTE_ACCESS_SLUG}></platform-api-menu>`,
              () => nothing,
            )}
          </slot>

          <slot name="remote-support-menu" slot="remote-support-menu">
            ${when(
              availableProducts.includes(REMOTE_SUPPORT_SLUG),
              () => html`<platform-api-menu product=${REMOTE_SUPPORT_SLUG}></platform-api-menu>`,
              () => nothing,
            )}
          </slot>

          <slot name="secrets-menu" slot="secrets-menu">
            ${when(
              availableProducts.includes(SECRETS_SLUG),
              () => html`<platform-secrets-menu></platform-secrets-menu>`,
              () => nothing,
            )}
          </slot>

          <slot name="entitle-menu" slot="entitle-menu">
            ${when(
              availableProducts.includes(ENTITLE_SLUG),
              () => html`<platform-api-menu product=${ENTITLE_SLUG}></platform-api-menu>`,
              () => nothing,
            )}
          </slot>

          <slot name="ai-coworkers-menu" slot="ai-coworkers-menu">
            ${when(
              availableProducts.includes(AI_COWORKERS_SLUG),
              () => html`<platform-api-menu product=${AI_COWORKERS_SLUG}></platform-api-menu>`,
              () => nothing,
            )}
          </slot>

          <slot name="reporting-insights-menu" slot="reporting-insights-menu">
            ${when(
              availableProducts.includes(INSIGHTS_SLUG),
              () => html`<platform-reporting-insights-menu></platform-reporting-insights-menu>`,
              () => nothing,
            )}
          </slot>
        </platform-menu>
        <platform-product-header-link
          activeRoute=${this.activeRoute || ''}
          .currentProduct=${this.currentProduct}
          .environmentLabel=${this.environmentLabel}
        >
        </platform-product-header-link>
      </div>

      <div class="platform-bar-end">
        <platform-site-selector
          selected=${this.site.site_id}
          data-dd-action-name="site-selector"
        ></platform-site-selector>
        <a href="${this.currentProduct.helpUrl}" aria-label="${msg('Help')}" target="_blank" rel="noopener noreferrer">
          <btux-svg name="circle-question"></btux-svg>
        </a>

        <platform-user-profile data-dd-action-name="user-profile-popout"></platform-user-profile>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [OVERLAY_MENU_NAVIGATION_SHELL_TAG]: PlatformOverlayMenuNavigationShell;
  }
}
